



































































































































































































































































































































































































































































































































































































































































import { Component, Vue, Ref, Prop } from 'vue-property-decorator';
const fabric = require('fabric').fabric;

type Coords = {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
};

@Component
export default class HonorScore extends Vue {
  @Ref() canvasEl: null | undefined | HTMLInputElement;
  @Prop({ default: 0 }) score!: number;
  @Prop({ default: 0 }) estimatedScore!: number;
  @Prop({ default: false }) readonly fullEarningAvailable!: boolean;
  @Prop({ default: '' }) readonly tooltipTitle!: string;
  @Prop({ default: '' }) readonly tooltipBody!: string;

  maxScore: number = 1200;
  svgRendered = false;
  canvas = new fabric.StaticCanvas();
  originalParsedObjects: any[] = [];
  arrowPoint: any;
  motionPaths = {
    arrow: null,
    arrowPath: null,
    negativeFill: null,
    negativeFillPath: null,
    generalRef: null,
    mask: null,
  } as Record<string, any>;

  get _score() {
    return Math.max(0, Math.min(this.score, this.maxScore));
  }

  get fullEarningsAvailable() {
    return this.score >= this.maxScore;
  }

  get decimalPercentage() {
    return this._score / this.maxScore;
  }

  get percentage() {
    return (this.decimalPercentage * 100).toFixed(2);
  }

  get indicatorClasses() {
    return {
      hs__indicator: true,
      'hs__indicator--full-earning': this.fullEarningAvailable,
    };
  }

  get estimationDirection() {
    let direction = 'neutral';
    if (this.estimatedScore > 0) {
      direction = 'up';
    } else if (this.estimatedScore < 0) {
      direction = 'down';
    }
    return direction;
  }

  get estimatedScoreBg() {
    let color = '#C9D6DF';
    if (this.estimationDirection === 'up') {
      color = '#75EB3C';
    } else if (this.estimationDirection === 'down') {
      color = '#E05A30';
    }
    return color;
  }

  get absEstimatedScore() {
    return Math.abs(this.estimatedScore);
  }

  get scoreToPercentage() {
    return +Math.floor((this._score / this.maxScore) * 100).toFixed(0);
  }

  async mounted() {
    fabric.Object.prototype.originX = fabric.Object.prototype.originY =
      'center';
    await this.loadSvg();
    this.loadRefObjects();
    this.originalParsedObjects
      .filter(
        obj =>
          ![
            'negativeFill',
            'negativeFillPath',
            'bottomSpacer',
            'mask',
          ].includes(obj.id),
      )
      .forEach(obj => this.canvas.add(obj));
    if (this._score) {
      this.setArrowPosition();
      this.loadNegativeFill();
    }
    this.findById('positiveFill')?.set({
      stroke: this._score ? '#3f8bea' : '#4f5b65',
    });
    this.motionPaths.arrow.bringToFront();
    this.canvas.renderAll();
  }

  updated() {
    this.setArrowPosition();
    if (this._score) {
      this.loadNegativeFill();
    }
    this.findById('positiveFill')?.set({
      stroke: this._score ? '#3f8bea' : '#4f5b65',
    });
    this.motionPaths.arrow.bringToFront();
    this.canvas.renderAll();
  }

  loadNegativeFill() {
    const existingNegativeFill = this.findById('negativeFill');
    const generalRef = this.motionPaths.generalRef;
    const startAngleCoords: Coords = {
      x1: generalRef.left,
      y1: generalRef.top,
      x2: this.arrowPoint.x,
      y2: this.arrowPoint.y,
    };
    const segments = fabric.util.getPathSegmentsInfo(
      this.motionPaths.arrowPath.path,
    );
    const endSegment = segments[segments.length - 1];
    const endAngleCoords: Coords = {
      x1: generalRef.left,
      y1: generalRef.top,
      x2: endSegment.x,
      y2: endSegment.y,
    };

    if (existingNegativeFill) {
      this.originalParsedObjects = this.originalParsedObjects.filter(
        obj => obj.id !== 'negativeFill',
      );
      this.canvas.remove(existingNegativeFill);
    }

    const negativeFill = new fabric.Circle({
      radius: generalRef.radius,
      fill: 'transparent',
      stroke: '#4f5b65',
      strokeWidth: 22.5,
      top: generalRef.top,
      left: generalRef.left,
      startAngle: this.angleInDegrees(startAngleCoords),
      endAngle: this.angleInDegrees(endAngleCoords),
      id: 'negativeFill',
    });

    this.originalParsedObjects.push(negativeFill);
    this.canvas.add(negativeFill);
  }

  setArrowPosition() {
    const { arrowPath, arrow } = this.motionPaths;
    const segments = fabric.util.getPathSegmentsInfo(arrowPath.path);

    let targetPosition: { x: number; y: number };

    if (!this._score) {
      targetPosition = { x: arrowPath.path[0][1], y: arrowPath.path[0][2] };
    } else {
      const totalLength = segments[segments.length - 1].length;
      const distanceToTravel = totalLength * this.decimalPercentage;

      targetPosition = fabric.util.getPointOnPath(
        arrowPath.path,
        distanceToTravel,
      );
    }

    this.arrowPoint = targetPosition;
    const totalRotationDegrees = 232;
    const rotation = totalRotationDegrees * this.decimalPercentage;
    arrow.set({
      left: targetPosition.x,
      top: targetPosition.y,
    });

    arrow.rotate(rotation);
    arrow.bringToFront();
  }

  loadRefObjects() {
    Object.keys(this.motionPaths).forEach(key => {
      this.motionPaths[key] = this.findById(key);
    });
  }

  findById(id: any) {
    return this.originalParsedObjects.find((obj: { id: any }) => obj.id === id);
  }

  async loadSvg() {
    const svg = document.getElementById('honor-score');

    this.canvas.initialize(this.canvasEl, {
      width: svg?.clientWidth,
      height: svg?.clientHeight,
      backgroundColor: 'transparent',
    });

    const svgString = new XMLSerializer().serializeToString(svg as Node);
    svg!.style.display = 'none';

    await new Promise<void>(resolve => {
      fabric.loadSVGFromString(svgString, (objects: any[]) => {
        this.originalParsedObjects = objects;
        resolve();
      });
    });

    this.svgRendered = true;
  }

  angleInDegrees({ x1, y1, x2, y2 }: Coords) {
    let dy = y2 - y1;
    let dx = x2 - x1;
    let theta = Math.atan2(dy, dx); // range (-PI, PI]
    theta *= 180 / Math.PI; // rads to degrees, range (-180, 180]
    if (theta < 0) theta = 360 + theta; // range [0, 360)
    return theta;
  }
}
